import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  addToCart,
  addToCartAsync,
  fetchCartAsync,
  selectCartItems,
  syncWithLocalStorage,
} from "../../Features/Cart/cartSlice.js";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Allmed.css";
import { RiSearchLine, RiFilterLine } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import df from '../../Assets/Brands/default.png'

const Allmedicine = () => {
  const userId = useSelector((state) => state.user.user?._id);
  const [medicines, setMedicines] = useState([]);
  const [visibleMedicines, setVisibleMedicines] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]); 
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [sortBy, setSortBy] = useState("default");
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);

  const fetchMedicines = async () => {
    try {
      const response = await axios.get("https://api.swasthmitra.com/api/medicine");
      setMedicines(response.data);
      setSearchResults(response.data);
      const uniqueCategories = [
        "all",
        ...new Set(response.data.map((medicine) => medicine.category)),
      ];
      setCategories(uniqueCategories);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch medicines. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicines();
  }, []);

  const handleViewMore = () => {
    setVisibleMedicines((prev) => prev + 8); // Show 8 more items each time
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleAddToCart = async (medicine) => {
    if (!userId) {
      toast.error("Please login to add items to cart");
      navigate('/login')
      return;
    }
    const cartItem = {
      productID: medicine._id,
      productName: medicine.name,
      dealRate: parseFloat(medicine.salePrice),
      productImage: medicine.imageUrl || medicine.imageUrls[0] || "",
      quantity: 1,
      batchNumber: medicine.batchNumber,
      brand: medicine.brand,
      packof: medicine.packOf,
      gst: medicine.gst,
      mrp: medicine.mrp,
      expiryDate: medicine.expiryDate,
    };

    try {
      await dispatch(
        addToCartAsync({
          userId: userId,
          productID: medicine._id,
          quantity: 1,
        })
      );
      dispatch(addToCart(cartItem));

      toast.success(`${medicine.name} added to cart!`, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add item to cart");
    }
  };

  const handleGoToCart = () => {
    // Redirect to the cart page (you may need to use a routing library like react-router-dom)
    window.location.href = "/cart";
  };

  const isItemInCart = (medicineId) => {
    return cartItems.some((item) => item.productID === medicineId);
  };

  // Add useEffect to fetch cart data on component mount
  useEffect(() => {
    if (userId && cartItems.length === 0) {
      dispatch(fetchCartAsync(userId));
    }
  }, [userId, dispatch]);

  // Handle search input change with real-time results
  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setShowDropdown(false);
      return;
    }
    
    // Search by name, brand, and salt
    const results = medicines.filter(
      (medicine) =>
        medicine.name.toLowerCase().includes(value) ||
        (medicine.brand && medicine.brand.toLowerCase().includes(value)) ||
        (medicine.salt && medicine.salt.toLowerCase().includes(value))
    );
    
    setSearchResults(results);
    setShowDropdown(results.length > 0);
  };

  // Handle search result click
  const handleResultClick = (medicine) => {
    navigate(`/product/${medicine._id}`);
    setShowDropdown(false);
    setSearchTerm('');
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.search-box')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDropdown]);

  // Add this sorting function
  const getSortedMedicines = (medicines) => {
    switch (sortBy) {
      case "price-low-high":
        return [...medicines].sort((a, b) => a.dealRate - b.dealRate);
      case "price-high-low":
        return [...medicines].sort((a, b) => b.dealRate - a.dealRate);
      case "name-a-z":
        return [...medicines].sort((a, b) => a.name.localeCompare(b.name));
      case "name-z-a":
        return [...medicines].sort((a, b) => b.name.localeCompare(a.name));
      default:
        return medicines;
    }
  };

  // Update the filteredMedicines const to include sorting and enhanced search
  const filteredMedicines = getSortedMedicines(
    medicines
      .filter((medicine) =>
        selectedCategory === "all"
          ? true
          : medicine.category === selectedCategory
      )
      .filter((medicine) =>
        medicine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (medicine.brand && medicine.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (medicine.salt && medicine.salt.toLowerCase().includes(searchTerm.toLowerCase()))
      )
  );

  // const getCategoryIcon = (category) => {
  //   switch (category.toLowerCase()) {
  //     case "tablets":
  //       return <RiMedicineBottleLine />;
  //     case "capsules":
  //       return <RiCapsuleLine />;
  //     case "syrups":
  //       return <RiTestTubeLine />;
  //     case "injections":
  //       return <RiSyringeLine />;
  //     case "first aid":
  //       return <RiFirstAidKitLine />;
  //     case "all":
  //       return <RiHeartPulseLine />;
  //     default:
  //       return <RiMedicineBottleLine />;
  //   }
  // };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="allmedicine-page"
    >
      <ToastContainer />

      <div className="allmedicine-container">
        {/* Search and Filter Section */}
        <motion.div
          initial={{ x: -100 }}
          animate={{ x: 0 }}
          className="search-filter-section"
        >
          <div className="search-box">
            <RiSearchLine className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, brand or salt..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => searchTerm.trim() !== '' && setShowDropdown(true)}
            />
            {showDropdown && searchTerm.trim() !== '' && (
              <div className="search-dropdown">
                {searchResults.length > 0 ? (
                  searchResults.slice(0, window.innerWidth >= 1024 ? 8 : 5).map((medicine) => (
                    <div 
                      key={medicine._id} 
                      className="search-result-item"
                      onClick={() => handleResultClick(medicine)}
                    >
                      <div className="search-result-image">
                        <img 
                          src={medicine.imageUrl || medicine.imageUrls?.[0] || df} 
                          alt={medicine.name} 
                        />
                      </div>
                      <div className="search-result-info">
                        <div className="search-result-name">{medicine.name}</div>
                        {medicine.brand && <div className="search-result-brand">Brand: {medicine.brand}</div>}
                        {medicine.salt && <div className="search-result-salt">Salt: {medicine.salt}</div>}
                        <div className="search-result-price">₹{Math.round(medicine.dealRate)} 
                          {medicine.dealRate < medicine.mrp && (
                            <span className="search-result-discount">
                              {Math.round(((medicine.mrp - medicine.dealRate) / medicine.mrp) * 100)}% OFF
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">No medicines found</div>
                )}
                {searchResults.length > (window.innerWidth >= 1024 ? 8 : 5) && (
                  <div className="search-view-all" onClick={() => setShowDropdown(false)}>
                    View all {searchResults.length} results
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add sorting dropdown */}
          <div className="sort-section">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="default">Sort By</option>
              <option value="price-low-high">Price: Low to High</option>
              <option value="price-high-low">Price: High to Low</option>
              <option value="name-a-z">Name: A to Z</option>
              <option value="name-z-a">Name: Z to A</option>
            </select>
          </div>
        </motion.div>

        {/* Categories Sidebar */}
        <motion.div
          initial={{ x: -100 }}
          animate={{ x: 0 }}
          className="allmedicine-categories"
        >
          <div className="category-header1">
            <h4>CATEGORIES</h4>
          </div>
          <motion.ul className="allmedicine-category-list">
            {categories.map((category) => (
              <motion.li
                key={category}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`allmedicine-category-item ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => handleCategorySelect(category)}
              >
                <span>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </span>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        {/* Products Grid */}
        <div className="allmedicine-products">
          <motion.div
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="allmedicine-header"
          ></motion.div>
          <div className="products-grid2">
            {filteredMedicines.slice(0, visibleMedicines).map((medicine) => (
              <div className="product-card1" key={medicine._id}>
                <div className="image-wrapper">
                  <Link to={`/product/${medicine?._id}`} onClick={scrollToTop}>
                    <img
                      src={
                        medicine.imageUrl ||
                        medicine.imageUrls[0] ||
                        df
                      }
                      alt={medicine.name}
                      className="allmedicine-image"
                    />
                  </Link>
                </div>
                <button
                  className={`view-details-btn1 ${
                    cartItems.some((item) => item.productID === medicine._id)
                      ? "disabled"
                      : ""
                  }`}
                  onClick={() => {
                    if (
                      !cartItems.some((item) => item.productID === medicine._id)
                    ) {
                      handleAddToCart(medicine);
                    }
                  }}
                >
                  {cartItems.some((item) => item.productID === medicine._id)
                    ? "In Cart"
                    : "Add to Cart"}
                </button>
                <div className="product-info">
                  <h4>
                    {medicine.name.length > 20
                      ? ` ${medicine.name.slice(0, 25)}...`
                      : medicine.name}
                  </h4>
                  <span className="category-tag">{medicine.category}</span>
                  <div className="price-info">
                    <span className="mrp2">₹{medicine.mrp}</span>
                    {/* <span className="deal-rate2">₹{medicine.dealRate}</span> */}
                    <span className="deal-rate2">
                      ₹{Math.round(medicine.dealRate)}
                    </span>
                    {medicine.dealRate < medicine.mrp && (
                      <span className="discount2">
                        {Math.round(
                          ((medicine.mrp - medicine.dealRate) / medicine.mrp) *
                            100
                        )}
                        % OFF
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {visibleMedicines < filteredMedicines.length && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="allmedicine-view-more"
              onClick={handleViewMore}
            >
              View More
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Allmedicine;
